import React, { useState, useEffect, useCallback } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isCodeNode, CODE_LANGUAGE_MAP, getLanguageFriendlyName } from '@lexical/code';
import { mergeRegister } from '@lexical/utils';
import type { NodeKey } from 'lexical';
import { $getNodeByKey, $getSelection, $isRangeSelection } from 'lexical';
import { styled } from 'baseui';
import { Select } from 'baseui/select';
import { SELECT_PROPS } from '../../constants/constants';
import { CODE_LANGUAGE_OPTIONS, SELECT_STYLE_OVERRIDES } from './constants/constants';
const TOP_OFFSET = 4;
const RIGHT_OFFSET = 8;
const LanguageSelect = styled<'div', {
  $styles?: {
    top?: string;
    right?: string;
  };
}>('div', ({
  $styles
}: {
  $styles?: {
    top?: string;
    right?: string;
  };
}) => ({
  position: 'absolute',
  top: $styles?.top || '0',
  right: $styles?.right || '0'
}));
const CodeLanguageSelect = ({
  anchorElem
}: {
  anchorElem: HTMLElement;
}) => {
  const [editor] = useLexicalComposerContext();
  const [isCodeNode, setIsCodeNode] = useState<boolean>(false);
  const [lang, setLang] = useState<string>('');
  const [selectedElementKey, setSelectedElementKey] = useState<NodeKey | null>(null);
  const [isEditable, setIsEditable] = useState<boolean>(() => editor.isEditable());
  const [position, setPosition] = useState<{
    top: string;
    right: string;
  }>({
    top: '0',
    right: '0'
  });
  const updateCodeLang = useCallback(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) {
      return;
    }
    const anchorNode = selection.anchor.getNode();
    const element = anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow();
    const elementKey = element.getKey();
    const elementDOM = editor.getElementByKey(elementKey);
    if (elementDOM === null || !$isCodeNode(element)) {
      setIsCodeNode(false);
      return;
    }
    setSelectedElementKey(elementKey);
    setIsCodeNode(true);
    const {
      y: editorElemY,
      right: editorElemRight
    } = anchorElem.getBoundingClientRect();
    const {
      y: codeElemY,
      right: codeElemRight
    } = elementDOM.getBoundingClientRect();
    setPosition({
      top: `${codeElemY - editorElemY + TOP_OFFSET}px`,
      right: `${editorElemRight - codeElemRight + RIGHT_OFFSET}px`
    });
    const language = (element.getLanguage() as keyof typeof CODE_LANGUAGE_MAP);
    setLang(language ? CODE_LANGUAGE_MAP[language] || language : '');
  }, [anchorElem, editor]);
  const onCodeLanguageSelect = useCallback((value: string) => {
    editor.update(() => {
      if (selectedElementKey === null) {
        return;
      }
      const node = $getNodeByKey(selectedElementKey);
      if ($isCodeNode(node)) {
        node.setLanguage(value);
      }
    });
  }, [editor, selectedElementKey]);
  useEffect(() => {
    return mergeRegister(editor.registerEditableListener(isEditable => {
      setIsEditable(isEditable);
    }), editor.registerUpdateListener(({
      editorState
    }) => {
      editorState.read(() => {
        updateCodeLang();
      });
    }));
  }, [updateCodeLang, editor]);
  return isCodeNode ? <LanguageSelect $styles={position}>
      <Select {...SELECT_PROPS} {...SELECT_STYLE_OVERRIDES} disabled={!isEditable} options={CODE_LANGUAGE_OPTIONS} labelKey="languageName" valueKey="languageType" onChange={({
      value
    }) => {
      onCodeLanguageSelect(value[0].languageType);
    }} value={[{
      languageType: lang,
      languageName: getLanguageFriendlyName(lang)
    }]} />
    </LanguageSelect> : null;
};
export default CodeLanguageSelect;